import React, { useState, useEffect, useRef } from "react";
import {
  formatCurrencyNetto,
  parseSwissNumber,
  STATUS_CONFIG,
  formatDateISO,
} from "./zahlungsplanUtils";

const ZahlungsplanDetailPopover = ({
  entry,
  anchorRect,
  globalSettings,
  onSave,
  onDelete,
  onClose,
  readOnly,
}) => {
  const popoverRef = useRef(null);
  const [formData, setFormData] = useState({
    netto_betrag: "",
    invoice_reference: "",
    status: "",
    wann: "",
    zahlungsfrist: "",
    name: "",
  });

  useEffect(() => {
    if (entry) {
      setFormData({
        netto_betrag: String(entry.netto_betrag || entry.betrag || ""),
        invoice_reference: entry.invoice_reference || "",
        status: entry.status || (entry.typ === "Einnahme" ? "expected" : "expecting"),
        wann: entry.wann || "",
        zahlungsfrist: String(entry.zahlungsfrist || globalSettings.zahlungsfrist || 30),
        name: entry.name || "",
      });
    }
  }, [entry, globalSettings.zahlungsfrist]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (!entry || !anchorRect) return null;

  // Position: prefer right side, fall back to left
  const viewportWidth = window.innerWidth;
  const popoverWidth = 320;
  const left =
    anchorRect.right + popoverWidth < viewportWidth
      ? anchorRect.right + 8
      : anchorRect.left - popoverWidth - 8;
  const top = Math.min(anchorRect.top, window.innerHeight - 400);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const amount = parseSwissNumber(formData.netto_betrag);
    if (isNaN(amount)) return;

    onSave(entry, {
      netto_betrag: amount,
      invoice_reference: formData.invoice_reference,
      status: formData.status,
      wann: formData.wann,
      zahlungsfrist: parseInt(formData.zahlungsfrist) || 30,
      name: formData.name,
    });
    onClose();
  };

  // Calculate Zahlungsziel
  const zahlungsziel = (() => {
    if (!formData.wann) return "";
    const d = new Date(formData.wann);
    d.setDate(d.getDate() + (parseInt(formData.zahlungsfrist) || 30));
    return formatDateISO(d);
  })();

  const incomeStatuses = ["expected", "sent", "paid", "overdue"];
  const expenseStatuses = ["expecting", "received", "entered_payment", "paid", "overdue"];
  const statusOptions = entry.typ === "Einnahme" ? incomeStatuses : expenseStatuses;

  return (
    <div
      className="zp-detail-popover"
      ref={popoverRef}
      style={{ position: "fixed", left, top }}
    >
      <div className="zp-detail-header">
        <h4>{entry.typ === "Einnahme" ? "Einnahme" : "Ausgabe"} Details</h4>
        <button className="zp-detail-close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="zp-detail-body">
        <div className="zp-detail-field">
          <label>Betrag (Netto)</label>
          <input
            type="text"
            value={formData.netto_betrag}
            onChange={(e) => handleChange("netto_betrag", e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="zp-detail-field">
          <label>Rechnungsnummer</label>
          <input
            type="text"
            value={formData.invoice_reference}
            onChange={(e) => handleChange("invoice_reference", e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="zp-detail-field">
          <label>Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            disabled={readOnly}
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {STATUS_CONFIG[s]?.label || s}
              </option>
            ))}
          </select>
        </div>

        <div className="zp-detail-field">
          <label>Rechnungsdatum</label>
          <input
            type="date"
            value={formData.wann}
            onChange={(e) => handleChange("wann", e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="zp-detail-field">
          <label>Zahlungsfrist (Tage)</label>
          <input
            type="number"
            value={formData.zahlungsfrist}
            onChange={(e) => handleChange("zahlungsfrist", e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="zp-detail-field">
          <label>Zahlungsziel</label>
          <span className="zp-detail-value">{zahlungsziel || "–"}</span>
        </div>

        <div className="zp-detail-field">
          <label>Bezeichnung</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {!readOnly && (
        <div className="zp-detail-actions">
          <button className="btn btn-danger" onClick={() => { onDelete(entry); onClose(); }}>
            Löschen
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Speichern
          </button>
        </div>
      )}
    </div>
  );
};

export default ZahlungsplanDetailPopover;
